import { DiscountType } from '@repo/common';
import { Button, Input, TableCell, TableRow } from '@repo/shadcn';
import { Minus, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { DeleteButton } from '@/components/ui/button';
import type { CartListItem } from '@/features/slice/cart';
import { useCartContext } from '@/modules/cart/cart.context.tsx';
// import { useDebounce } from '@uidotdev/usehooks'
import {
    calculateItemTotalPrice,
    formatCurrencyInBDT,
    logError,
} from '@/utils';

type CartItemProps = {
    item: CartListItem;
};

export const CartItem = ({ item }: CartItemProps) => {
    const { removeCartItem, isRemoveItemLoading, handleUpdateCartItem } =
        useCartContext();

    const [quantityState, setQuantityState] = useState(
        item.quantity.toString(),
    );

    // useEffect(() => {
    //     const quantity = parseInt(quantityState, 10);
    //     if (isNaN(quantity) || quantity < 0) {
    //         return;
    //     }

    //     handleUpdateCartItem(item.id, quantity);
    // }, [quantityState, item.id, handleUpdateCartItem]);

    const product_name = item.product_name || 'Unnamed Product';
    const quantity = item.quantity || 0;
    const price = item.price || 0;
    const discount =
        item.discount_type === DiscountType.Percentage
            ? `${item.discount_value || 0}%`
            : formatCurrencyInBDT(item.discount_value || 0);
    const total = calculateItemTotalPrice(
        price,
        quantity,
        item.discount_value || 0,
        item.discount_type,
    );

    const renderQuantity = () => {
        return (
            <TableCell className="flex items-center justify-end gap-1 text-right">
                <Button
                    size="icon"
                    variant="outline"
                    type="button"
                    className="border-2 border-orange-300 hover:border-orange-400"
                    onClick={() => {
                        setQuantityState((prev) => {
                            const newQuantity = parseInt(prev, 10) - 1;
                            return newQuantity.toString();
                        });
                        handleUpdateCartItem(item.id, {
                            quantity: parseInt(quantityState, 10) - 1,
                            discount_value: item.discount_value,
                            discount_type: item.discount_type as DiscountType,
                            price: item.price,
                        });
                    }}
                >
                    <Minus />
                </Button>
                <Input
                    value={quantityState}
                    onChange={(e) => {
                        setQuantityState(e.target.value);

                        const quantity = parseInt(e.target.value, 10);
                        if (isNaN(quantity) || quantity < 0) {
                            return;
                        }

                        handleUpdateCartItem(item.id, {
                            quantity,
                            discount_value: item.discount_value,
                            discount_type: item.discount_type as DiscountType,
                            price: item.price,
                        });
                    }}
                    className="w-20 text-right"
                />

                <Button
                    size="icon"
                    variant="outline"
                    type="button"
                    className="border-2 border-green-300 hover:border-green-400 focus:right-0"
                    onClick={() => {
                        setQuantityState((prev) => {
                            const newQuantity = parseInt(prev, 10) + 1;
                            return newQuantity.toString();
                        });
                        handleUpdateCartItem(item.id, {
                            quantity: parseInt(quantityState, 10) + 1,
                            discount_value: item.discount_value,
                            discount_type: item.discount_type as DiscountType,
                            price: item.price,
                        });
                    }}
                >
                    <Plus />
                </Button>
            </TableCell>
        );
    };

    const handleDelete = async () => {
        try {
            await removeCartItem(item.id).unwrap();
        } catch (error) {
            logError('CartItem:handleDelete', error);
            toast.error('Something went wrong!');
        }
    };

    return (
        <TableRow>
            <TableCell>{product_name}</TableCell>
            <TableCell className="text-right">
                {formatCurrencyInBDT(price)}
            </TableCell>
            {renderQuantity()}
            <TableCell className="text-right">{discount}</TableCell>
            <TableCell className="text-right">
                {formatCurrencyInBDT(price * quantity)}
            </TableCell>
            <TableCell className="text-right">
                {formatCurrencyInBDT(total)}
            </TableCell>
            <TableCell className="text-right">
                <DeleteButton
                    isIconButton
                    onClick={handleDelete}
                    isLoading={isRemoveItemLoading}
                />
            </TableCell>
        </TableRow>
    );
};

CartItem.displayName = 'CartItem';
